import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { State, Store } from '@ngrx/store';
import {
  CustomerServiceNoteService,
  Driver,
  DriverBankService,
  DriverBankTransaction,
  DriverManagementService,
  DriverService,
  DriverSuspensionService,
  PersonService,
  Trip,
  UserService
} from '@uzurv/clara';
import {
  DriverBankTransactionReasonConstants,
  DriverBankTransactionTypeConstants,
  FileData,
  ObjectConstants,
  PermissionsConstants,
  SafeUrlPipe,
  ServiceException,
  isNullOrUndefined
} from '@uzurv/core';
import {
  DriverService as DotsDriverService,
  MvrDetails,
  RandomScreeningDriverSelectionService,
  RefusalReasonService,
  Screening,
  ScreeningPackageConstants,
  SubstanceScreening,
  Vehicle
} from '@uzurv/dots';
import { AuthDataService } from '@uzurv/http';
import { of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AlertDialogComponent } from '../../components/dialogs/alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../../components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { CreateRandomSubstanceScreeningDialogComponent } from '../../components/dialogs/create-random-substance-screening-dialog/create-random-substance-screening-dialog.component';
import { SelectDialogComponent } from '../../components/dialogs/select-dialog/select-dialog.component';
import * as DriverActions from '../../store/driver/driver.actions';
import { DriverState } from './driver.reducer';
import { driverState } from './driver.selectors';
import { EditDriverProfileDialogComponent } from '../../components/dialogs/edit-driver-profile-dialog/edit-driver-profile-dialog.component';
import { AddressValidationDialogComponent } from '../../components/dialogs/address-validation-dialog/address-validation-dialog.component';
import { VerifiedAddress } from 'projects/sdk/packages/dots/src/models/verified-address';
import { Router } from '@angular/router';

@Injectable()
export class DriverEffects {
  start$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DriverActions.Start),
      switchMap(tabConfig => {
        const driverId = +tabConfig.routeParams['id'];
        if (isNullOrUndefined(driverId) || isNaN(driverId)) {
          return of();
        }
        return of(DriverActions.GetDriver({ driverId }));
      })
    )
  );

  getDriverBankStatements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverPayStatements),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) => {
        if (action.startDate > action.endDate) {
          return of(DriverActions.GetDriverPayStatementsFail({ error: new Error('Start date must be before end date') }));
        }
        return this.driverManagementService.getDriverPayStatements(state.driver.id, action.startDate, action.endDate).pipe(
          map(driverPayStatement => {
            const link = document.createElement('a');
            link.href = driverPayStatement.getDataURLString();
            link.setAttribute('download', driverPayStatement.filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return DriverActions.GetDriverPayStatementsSuccess();
          }),
          catchError(err => {
            return of(DriverActions.GetDriverPayStatementsFail({ error: err }));
          })
        );
      })
    );
  });

  searchDrivers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.DriverSearch, DriverActions.ShowMoreDriverSearchResults),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.driverService.searchForDriver(state.driverSearchString, state.driverSearchPageNumber, state.driverSearchPageSize).pipe(
          map((drivers: Array<Driver>) => DriverActions.DriverSearchSuccess({ drivers })),
          catchError(error => of(DriverActions.DriverSearchFail({ error })))
        )
      )
    );
  });

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          DriverActions.DriverSearchFail,
          DriverActions.GetDriverFail,
          DriverActions.GetVehiclesFail,
          DriverActions.GetDriverReservationsFail,
          DriverActions.GetDriverProfilePictureFail,
          DriverActions.GetDriverBankFail,
          DriverActions.GetDriverBankTransactionCreditReasonsFail,
          DriverActions.GetDriverBankTransactionDebitReasonsFail,
          DriverActions.GetCustomerServiceNotesFail,
          DriverActions.CreateCustomerServiceNoteForDriverFail,
          DriverActions.GetDriverBankTransactionsFail,
          DriverActions.CreateDriverBankTransactionForDriverFail,
          DriverActions.GetDriverAddressFail,
          DriverActions.GetDriverSuspensionsFail,
          DriverActions.GetDeactivationReasonsFail,
          DriverActions.ViewReferrerFail,
          DriverActions.SearchForReferrerFail,
          DriverActions.SetReferrerFail,
          DriverActions.CancelScreeningFail,
          DriverActions.GetSubstanceScreeningFail,
          DriverActions.GetMVRScreeningDetailsFail,
          DriverActions.ChangeFacilityFail,
          DriverActions.CreateSubstanceScreeningFail,
          DriverActions.GetDriverAnnualRideExecutionDataFail,
          DriverActions.GetDriverTaxDocumentsFail,
          DriverActions.EditDriverProfileFail,
          DriverActions.GetDriverPayStatementsFail
        ),
        map(action => {
          if (action?.error?.message.includes('Start date must be before end date')) {
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Error',
                message: 'Start date must be before end date'
              }
            });
          } else {
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Error',
                message: action.error.message
              }
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  getDrivers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriver),
      switchMap(action =>
        this.driverManagementService.findDriverById(action.driverId).pipe(
          map((driver: Driver) => DriverActions.GetDriverSuccess({ driver })),
          catchError(error => of(DriverActions.GetDriverFail({ error })))
        )
      )
    );
  });

  selectDriver$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DriverActions.DriverSelected),
        map(action => {
          this.router.navigate([`/driver-management/driver-detail/${action.driver.id}`]);
        })
      );
    },
    { dispatch: false }
  );

  editDriver$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.EditDriverProfile),
      switchMap(action => {
        return this.personService.updateDriver(action.driver.id, action.driver).pipe(
          switchMap(() => {
            return this.dotsDriverService.validateAndSaveAddressForDriver(action.driver.dotsDriverIdentifier, action.address).pipe(
              map((verifiedDriverAddress: VerifiedAddress) => {
                if (verifiedDriverAddress.requestedAddress.verified === true) {
                  return DriverActions.EditDriverProfileSuccess({
                    driver: action.driver,
                    address: verifiedDriverAddress.requestedAddress,
                    displayAddressValidationDialog: false
                  });
                }

                if (action.displayAddressValidationDialog === false) {
                  return DriverActions.EditDriverProfileSuccess({
                    driver: action.driver,
                    verifiedAddress: verifiedDriverAddress,
                    address: action.address,
                    displayAddressValidationDialog: false
                  });
                }

                return DriverActions.EditDriverProfileSuccess({
                  driver: action.driver,
                  verifiedAddress: verifiedDriverAddress,
                  address: action.address,
                  displayAddressValidationDialog: true
                });
              }),
              catchError(error => {
                return of(DriverActions.EditDriverProfileFail({ error }));
              })
            );
          }),
          catchError(error => of(DriverActions.EditDriverProfileFail({ error })))
        );
      })
    );
  });

  openConfirmAddressDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DriverActions.EditDriverProfileSuccess),
        filter(action => action.displayAddressValidationDialog),
        map(action => {
          if (!action.verifiedAddress.requestedAddress.verified)
            return this.dialog.open(AddressValidationDialogComponent, {
              data: {
                driver: action.driver,
                verifiedAddress: action.verifiedAddress
              }
            });
        })
      );
    },
    { dispatch: false }
  );

  openEditDriverProfileDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DriverActions.OpenEditDriverProfileDialog),
        withLatestFrom(this.store$.select(driverState)),
        map(([action, state]) => {
          this.dialog.open(EditDriverProfileDialogComponent, {
            data: {
              driver: isNullOrUndefined(action.driver) ? state.driver : action.driver,
              driverAddress: isNullOrUndefined(action.address) ? state.driverAddress : action.address,
              driverStateAddress: state.driverAddress
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  getVehicles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetVehicles),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.dotsDriverService.listVehiclesByDriverId(state.driver.dotsDriverIdentifier).pipe(
          map((vehicles: Array<Vehicle>) => DriverActions.GetVehiclesSuccess({ vehicles })),
          catchError(error => of(DriverActions.GetVehiclesFail({ error })))
        )
      )
    );
  });

  getReservations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverReservations, DriverActions.ReservationsPagination),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.driverManagementService
          .listReservationsByDriverId(state.driver.id, state.reservationsPageIndex, state.reservationsPageSize)
          .pipe(
            map((reservations: Array<Trip>) => DriverActions.GetDriverReservationsSuccess({ reservations })),
            catchError(error => of(DriverActions.GetDriverReservationsFail({ error })))
          )
      )
    );
  });

  getDriverScreenings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        DriverActions.GetDriverScreenings,
        DriverActions.DriverScreeningsPagination,
        DriverActions.CancelScreeningSuccess,
        DriverActions.CreateSubstanceScreeningSuccess
      ),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) => {
        return this.dotsDriverService
          .listScreeningsByDriverId(state.driver.dotsDriverIdentifier, state.driverScreeningsPageIndex, state.driverScreeningsPageSize)
          .pipe(
            map((screenings: Array<Screening>) => {
              return DriverActions.GetDriverScreeningsSuccess({ screenings });
            }),
            catchError(error => of(DriverActions.GetDriverScreeningsFail({ error })))
          );
      })
    );
  });

  confirmCancelScreening = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ConfirmCancelScreening),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to cancel this test?`,
              dismissText: `Dismiss`,
              isDestructive: false
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return of(
                  DriverActions.CancelScreening({
                    payload: { dotsDriverId: state.driver.dotsDriverIdentifier, screeningId: action.screeningId }
                  })
                );
              } else {
                return of(DriverActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  cancelSubstanceScreening = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.CancelScreening),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) =>
        this.dotsDriverService.cancelSubstanceScreening(action.payload.dotsDriverId, action.payload.screeningId).pipe(
          map(() => DriverActions.CancelScreeningSuccess()),
          catchError(error => of(DriverActions.CancelScreeningFail({ error })))
        )
      )
    );
  });

  confirmChangeFacility$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ConfirmChangeFacility),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to allow the driver to change facilities?`,
              dismissText: `Dismiss`,
              isDestructive: false
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return of(DriverActions.ChangeFacility({ dotsDriverId: action.dotsDriverId, screeningId: action.screeningId }));
              } else {
                return of(DriverActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  resendScreeningRequest = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ResendScreeningRequest),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) => {
        return this.dotsDriverService
          .createSubstanceScreening(
            state.driver.dotsDriverIdentifier,
            encodeURIComponent(action.screeningPackageCode),
            action.screeningReasonCode
          )
          .pipe(
            map((screening: Screening) => {
              this.store$.dispatch(DriverActions.GetDriverScreenings());
              return DriverActions.ResendScreeningRequestSuccess({ screening });
            }),
            catchError(error => of(DriverActions.ResendScreeningRequestFail({ error })))
          );
      })
    );
  });

  changeFacility = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ChangeFacility),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) =>
        this.dotsDriverService.changeSubstanceScreeningCollectionSite(state.driver.dotsDriverIdentifier, action.screeningId).pipe(
          map(result => {
            this.store$.dispatch(DriverActions.GetDriverScreenings());
            return DriverActions.ChangeFacilitySuccess();
          }),
          catchError(error => of(DriverActions.ChangeFacilityFail({ error })))
        )
      )
    );
  });

  expandScreening = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ExpandScreening),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action]) => {
        if (action.screening.screeningPackage.code === ScreeningPackageConstants.SCREENING_PACKAGE_MVR_CODE) {
          return of(DriverActions.GetMVRScreeningDetails({ screening: action.screening }));
        }
        return of(DriverActions.GetSubstanceScreening({ screening: action.screening }));
      })
    );
  });

  getSubstanceScreening = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetSubstanceScreening),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) => {
        if (state.expandedScreening) {
          return this.dotsDriverService.getSubstanceScreeningDetails(state.driver.dotsDriverIdentifier, state.expandedScreening.id).pipe(
            map((substanceScreening: SubstanceScreening) => {
              return DriverActions.GetSubstanceScreeningSuccess({ substanceScreening });
            }),

            catchError(error => of(DriverActions.GetSubstanceScreeningFail({ error })))
          );
        } else {
          return of(DriverActions.AbandonSubmission());
        }
      })
    );
  });

  getMVRScreeningDetails = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetMVRScreeningDetails),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) => {
        if (state.expandedScreening) {
          return this.dotsDriverService.getMVRScreeningDetails(state.driver.dotsDriverIdentifier, state.expandedScreening.id).pipe(
            map((mvrOrder: MvrDetails) => {
              return DriverActions.GetMVRScreeningDetailsSuccess({ mvrOrderDetails: mvrOrder });
            }),
            catchError(error => of(DriverActions.GetMVRScreeningDetailsFail({ error })))
          );
        } else {
          return of(DriverActions.AbandonSubmission());
        }
      })
    );
  });

  getMVRReport = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetMVRReport),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) => {
        return this.dotsDriverService.getMVRReport(state.driver.dotsDriverIdentifier, action.screeningId).pipe(
          map((mvrDetails: FileData) => {
            if (isNullOrUndefined(mvrDetails)) {
              throw new ServiceException('Error Mvr Report Null');
            }

            const blob = new Blob([atob(mvrDetails.blob)], { type: mvrDetails.mimeType });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', mvrDetails.filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            return DriverActions.GetMVRReportSuccess();
          }),
          catchError(error => of(DriverActions.GetMVRReportFail({ error })))
        );
      })
    );
  });

  createSubstanceScreening = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.CreateSubstanceScreening),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) => {
        return this.dotsDriverService
          .createSubstanceScreening(
            state.driver.dotsDriverIdentifier,
            encodeURIComponent(action.screeningPackageCode),
            action.screeningReasonCode
          )
          .pipe(
            map(() => DriverActions.CreateSubstanceScreeningSuccess()),
            catchError(error => of(DriverActions.CreateSubstanceScreeningFail({ error })))
          );
      })
    );
  });

  createRandomSubstanceScreening = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.CreateRandomSubstanceScreening),
      switchMap(action => {
        return this.randomScreeningDriverSelectionService
          .createRandomSubstanceScreening(action.dotsDriverId, encodeURIComponent(action.screeningPackageCode))
          .pipe(
            map(() => DriverActions.CreateSubstanceScreeningSuccess()),
            catchError(error => of(DriverActions.CreateSubstanceScreeningFail({ error })))
          );
      })
    );
  });
  getDriverProfilePictureUrl$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverProfilePicture, DriverActions.GetDriverSuccess),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.dotsDriverService.getProfilePictureForDriver(state.driver.dotsDriverIdentifier).pipe(
          map((fileData: FileData) =>
            DriverActions.GetDriverProfilePictureSuccess({
              safeUrl: fileData && fileData.getDataURLString() ? this.safeUrlPipe.transform(fileData.getDataURLString()) : null
            })
          ),
          catchError(error => of(DriverActions.GetDriverProfilePictureFail({ error })))
        )
      )
    );
  });

  getDriverBank$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverBank, DriverActions.GetDriverSuccess, DriverActions.CreateDriverBankTransactionForDriverSuccess),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.driverBankService.getDriverBankByDriverId(state.driver.id).pipe(
          map(driverBank => DriverActions.GetDriverBankSuccess({ driverBank })),
          catchError(error => of(DriverActions.GetDriverBankFail({ error })))
        )
      )
    );
  });

  getDriverBankTransactionCreditReasons$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverSuccess, DriverActions.GetDriverBankTransactionCreditReasons),
      switchMap(() =>
        this.driverBankService.listDriverBankTransactionCreditReasons().pipe(
          map(driverBankCreditTransactionReasons =>
            DriverActions.GetDriverBankTransactionCreditReasonsSuccess({
              driverBankTransactionCreditReasons: driverBankCreditTransactionReasons
            })
          ),
          catchError(error => of(DriverActions.GetDriverBankTransactionCreditReasonsFail({ error })))
        )
      )
    );
  });

  getDriverBankTransactionDebitReasons$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverSuccess, DriverActions.GetDriverBankTransactionDebitReasons),
      switchMap(() =>
        this.driverBankService.listDriverBankTransactionDebitReasons().pipe(
          map(driverBankDebitTransactionReasons =>
            DriverActions.GetDriverBankTransactionDebitReasonsSuccess({
              driverBankTransactionDebitReasons: driverBankDebitTransactionReasons
            })
          ),
          catchError(error => of(DriverActions.GetDriverBankTransactionDebitReasonsFail({ error })))
        )
      )
    );
  });

  getCustomerServiceNotes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        DriverActions.CreateCustomerServiceNoteForDriverSuccess,
        DriverActions.CreateTransactionNoteSuccess,
        DriverActions.GetCustomerServiceNotes,
        DriverActions.AddCustomerServiceNotesPage
      ),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.customerServiceNoteService
          .getCustomerServiceNotesForObjectIdAndName(
            state.driver.id,
            ObjectConstants.PERSON,
            state.customerServiceNotesPageNumber,
            state.customerServiceNotesPageSize
          )
          .pipe(
            map(notes => DriverActions.GetCustomerServiceNotesSuccess({ notes })),
            catchError(error => of(DriverActions.GetCustomerServiceNotesFail({ error })))
          )
      )
    );
  });

  createCustomerServiceNoteForDriver$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.CreateCustomerServiceNoteForDriver),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) => {
        action.customerServiceNote.objectId = state.driver.id;
        action.customerServiceNote.objectName = ObjectConstants.PERSON;
        return this.customerServiceNoteService.createCustomerServiceNote(action.customerServiceNote).pipe(
          map(() => DriverActions.CreateCustomerServiceNoteForDriverSuccess()),
          catchError(error => of(DriverActions.CreateCustomerServiceNoteForDriverFail({ error })))
        );
      })
    );
  });

  getDriverBankTransactions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        DriverActions.CreateDriverBankTransactionForDriverSuccess,
        DriverActions.DriverBankTransactionFiltersChanged,
        DriverActions.GetDriverBankTransactions,
        DriverActions.DriverBankTransactionsPagination
      ),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.driverBankService
          .listDriverBankTransactionsByDriverId(
            state.driver.id,
            state.bankTransactionsStartDate,
            state.bankTransactionsEndDate,
            state.bankTransactionsPageNumber,
            state.bankTransactionsPageSize
          )
          .pipe(
            map(bankTransactions => DriverActions.GetDriverBankTransactionsSuccess({ bankTransactions })),
            catchError(error => of(DriverActions.GetDriverBankTransactionsFail({ error })))
          )
      )
    );
  });

  selectGetTransactionReason$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.SelectGetTransactionReason),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) =>
        this.dialog
          .open(SelectDialogComponent, {
            data: {
              title: 'What is the reason for this transaction?',
              question: '',
              list:
                action.bankTransactionData.driverBankTransactionType === DriverBankTransactionTypeConstants.CREDIT
                  ? state.driverBankTransactionCreditReasons
                  : state.driverBankTransactionDebitReasons,
              accessor: 'name',
              prompt: 'Transaction reason'
            }
          })
          .afterClosed()
          .pipe(
            map(driverBankTransactionReason => {
              if (driverBankTransactionReason) {
                return DriverActions.SelectGetTransactionReasonSuccess({
                  payload: {
                    transactionReason: driverBankTransactionReason,
                    bankTransactionData: action.bankTransactionData
                  }
                });
              } else {
                return DriverActions.AbandonSubmission();
              }
            })
          )
      )
    );
  });

  confirmCreateNewTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.SelectGetTransactionReasonSuccess),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to ${action.payload.bankTransactionData.driverBankTransactionType} this driver bank?`,
              isDestructive: false
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return this.driverBankService
                  .findDriverBankTransactionTypeByName(action.payload.bankTransactionData.driverBankTransactionType)
                  .pipe(
                    map(type => {
                      const driverBankTransaction = new DriverBankTransaction();
                      driverBankTransaction.driverBankId = state.driverBank.id;
                      driverBankTransaction.amount = action.payload.bankTransactionData.amount;
                      driverBankTransaction.description = action.payload.bankTransactionData.description;
                      driverBankTransaction.driverBankTransactionReason = action.payload.transactionReason;
                      driverBankTransaction.driverBankTransactionType = type;

                      return DriverActions.CreateDriverBankTransactionForDriver({
                        payload: { driverBankTransaction: driverBankTransaction, reverseTransaction: false }
                      });
                    }),
                    catchError(error => of(DriverActions.CreateDriverBankTransactionForDriverFail({ error })))
                  );
              } else {
                return of(DriverActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  createDriverBankTransactionForDriver$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.CreateDriverBankTransactionForDriver),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([action, state]) =>
        this.driverBankService.createDriverBankTransactionForDriver(action.payload.driverBankTransaction, state.driver).pipe(
          map(() =>
            DriverActions.CreateDriverBankTransactionForDriverSuccess({
              payload: {
                driverBankTransaction: action.payload.driverBankTransaction,
                reverseTransaction: false
              }
            })
          ),
          catchError(error => of(DriverActions.CreateDriverBankTransactionForDriverFail({ error })))
        )
      )
    );
  });

  confirmReverseTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ConfirmReverseTransaction),
      switchMap(action =>
        this.dialog

          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: 'Are you sure you want to reverse this transaction?',
              isDestructive: false
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirm => {
              if (confirm) {
                const transactionDescription =
                  'Reversed Transaction: ' +
                  action.driverBankTransaction.driverBankTransactionReason.name +
                  ', ' +
                  this.currencyPipe.transform(action.driverBankTransaction.amount, 'USD', 'symbol');

                let transactionReasonName: string;
                let transactionTypeName: string;
                if (action.driverBankTransaction.driverBankTransactionType.name === DriverBankTransactionTypeConstants.CREDIT) {
                  transactionReasonName = DriverBankTransactionReasonConstants.MISC_DEBIT;
                  transactionTypeName = DriverBankTransactionTypeConstants.DEBIT;
                } else {
                  transactionReasonName = DriverBankTransactionReasonConstants.MISC_CREDIT;
                  transactionTypeName = DriverBankTransactionTypeConstants.CREDIT;
                }

                return this.driverBankService.findDriverBankTransactionTypeByName(transactionTypeName).pipe(
                  switchMap(driverBankTransactionType => {
                    action.driverBankTransaction.driverBankTransactionType = driverBankTransactionType;
                    return this.driverBankService.findDriverBankTransactionReasonByName(transactionReasonName).pipe(
                      map(driverBankTransactionReason => {
                        action.driverBankTransaction.driverBankTransactionReason = driverBankTransactionReason;
                        action.driverBankTransaction.description = transactionDescription;
                        return DriverActions.CreateDriverBankTransactionForDriver({
                          payload: {
                            driverBankTransaction: action.driverBankTransaction,
                            reverseTransaction: true
                          }
                        });
                      })
                    );
                  }),
                  catchError(error => of(DriverActions.CreateDriverBankTransactionForDriverFail({ error })))
                );
              } else {
                return of(DriverActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  getDriverAddress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverAddress, DriverActions.GetDriverSuccess),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.dotsDriverService.findAddressByDriverId(state.driver.dotsDriverIdentifier).pipe(
          map(address => DriverActions.GetDriverAddressSuccess({ address })),
          catchError(error => of(DriverActions.GetDriverAddressFail({ error })))
        )
      )
    );
  });

  goToDriverMap$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DriverActions.GoToDriverAddress),
        withLatestFrom(this.store$.select(driverState)),
        map(([_, state]) =>
          window.open(
            `https://www.google.com/maps/place/${state.driverAddress.streetAddress1},${state.driverAddress.city}, ${state.driverAddress.stateCode} ${state.driverAddress.zipCode}`,
            '_blank'
          )
        )
      );
    },
    { dispatch: false }
  );

  goToDocuments$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DriverActions.GoToDocuments),
        withLatestFrom(this.store$.select(driverState)),
        map(([_, state]) => {
          this.router.navigate([`/onboarding/drivers/${state.driver.dotsDriverIdentifier}`]);
        })
      );
    },
    { dispatch: false }
  );

  getDriverSuspensions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverSuccess, DriverActions.GetDriverSuspensions),
      withLatestFrom(this.store$.select(driverState)),
      filter(([_, state]) => this.authDataService.hasPermission({ any: [PermissionsConstants.DRIVER_SUSPENSION_READ], all: [] })),
      switchMap(([_, state]) =>
        this.driverSuspensionService.getDriverSuspensions(state.driver.id, 0, 20, false).pipe(
          map(driverSuspensions => DriverActions.GetDriverSuspensionsSuccess({ driverSuspensions })),
          catchError(error => of(DriverActions.GetDriverSuspensionsFail({ error })))
        )
      )
    );
  });

  getDeactivationReasons$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDeactivationReasons, DriverActions.GetDriverSuccess),
      switchMap(() =>
        this.userService.getDeactivationReasons().pipe(
          map(deactivationReasons => DriverActions.GetDeactivationReasonsSuccess({ deactivationReasons })),
          catchError(error => of(DriverActions.GetDeactivationReasonsFail({ error })))
        )
      )
    );
  });

  goToDriverSuspensions$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DriverActions.GoToDriverSuspensions),
        withLatestFrom(this.store$.select(driverState)),
        map(([_, state]) => {
          this.router.navigate([`/driver-suspensions/${state.driver.id}`]);
        })
      );
    },
    { dispatch: false }
  );

  viewReferrer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ViewReferrer),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.personService.findReferrerForPersonWithId(state.driver.id).pipe(
          map(person => DriverActions.ViewReferrerSuccess({ referrer: person })),
          catchError(error => of(DriverActions.ViewReferrerFail({ error })))
        )
      )
    );
  });

  searchForReferrer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.SearchForReferrer, DriverActions.ShowMoreReferrers),
      withLatestFrom(this.store$.select(driverState)),
      debounceTime(500),
      filter(([action, _]) => action.searchString.length > 2),
      switchMap(([action, state]) =>
        this.personService.searchPerson(action.searchString, state.referrerSearchPageNumber, state.referrerSearchPageSize).pipe(
          map(potentialReferrers => DriverActions.SearchForReferrerSuccess({ referrerSearchResults: potentialReferrers })),
          catchError(error => of(DriverActions.SearchForReferrerFail({ error })))
        )
      )
    );
  });

  setReferrer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.SetReferrer),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.personService.setReferrerForPersonWithId(state.driver.id, state.selectedReferrer.id).pipe(
          map(() => DriverActions.SetReferrerSuccess({ referrer: state.selectedReferrer })),
          catchError(error => of(DriverActions.SetReferrerFail({ error })))
        )
      )
    );
  });

  getAnnualRideExecutionData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverAnnualRideExecutionData, DriverActions.GetDriverSuccess),
      withLatestFrom(this.store$.select(driverState)),
      filter(([_, state]) => this.authDataService.hasPermission({ any: [PermissionsConstants.DRIVER_TAX_INFORMATION_READ], all: [] })),
      switchMap(([_, state]) =>
        this.driverManagementService.listAnnualRideExecutionDataByDriverId(state.driver.id).pipe(
          map(driverAnnualRideExecutionData => DriverActions.GetDriverAnnualRideExecutionDataSuccess({ driverAnnualRideExecutionData })),
          catchError(error => of(DriverActions.GetDriverAnnualRideExecutionDataFail({ error })))
        )
      )
    );
  });

  getRandomScreeningYearlyProgress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetRandomScreeningYearlyProgress),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(() =>
        this.randomScreeningDriverSelectionService.getRandomScreeningYearlyProgress().pipe(
          map(randomScreeningYearlyProgress => {
            return DriverActions.GetRandomScreeningYearlyProgressSuccess({ randomScreeningYearlyProgress });
          }),
          catchError(error => of(DriverActions.GetRandomScreeningYearlyProgressFail({ error })))
        )
      )
    );
  });

  getScreeningDriverSelectionInfos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        DriverActions.GetRandomScreeningDriverSelectionInfos,
        DriverActions.CancelScreeningSuccess,
        DriverActions.ChangeFacilitySuccess,
        DriverActions.CreateSubstanceScreeningSuccess
      ),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(() =>
        this.randomScreeningDriverSelectionService.listSelectedDrivers().pipe(
          map(randomScreeningDriverSelections => {
            return DriverActions.GetRandomScreeningDriverSelectionInfosSuccess({
              randomScreeningDriverSelectionInfos: randomScreeningDriverSelections
            });
          }),
          catchError(error => of(DriverActions.GetRandomScreeningDriverSelectionInfosFail({ error })))
        )
      )
    );
  });

  getRandomScreeningDriverSelection = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetRandomScreeningDriverSelection),
      switchMap(action =>
        this.randomScreeningDriverSelectionService
          .getRandomScreeningDriverSelectionById(action.randomScreeningDriverSelectionInfo.randomScreeningDriverSelectionId)
          .pipe(
            map(randomScreeningDriverSelection => {
              return DriverActions.GetRandomScreeningDriverSelectionSuccess({ randomScreeningDriverSelection });
            }),
            catchError(error => of(DriverActions.GetRandomScreeningDriverSelectionFail({ error })))
          )
      )
    );
  });

  cancelRandomSubstanceScreening = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.CancelRandomScreening),
      switchMap(action => {
        return this.randomScreeningDriverSelectionService.updateRandomScreeningDriverSelection(action.randomScreeningDriverSelection).pipe(
          map(() => {
            this.dialog.closeAll();
            return DriverActions.CancelScreeningSuccess();
          }),
          catchError(error => of(DriverActions.CancelScreeningFail({ error })))
        );
      })
    );
  });

  getRandomScreeningConfigurationValues$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetRandomScreeningConfigurationValues),
      switchMap(() =>
        this.randomScreeningDriverSelectionService.listConfigurationValues().pipe(
          map(randomScreeningRuleValues => {
            return DriverActions.GetRandomScreeningConfigurationValuesSuccess({
              randomScreeningConfigurationValues: randomScreeningRuleValues
            });
          }),
          catchError(error => of(DriverActions.GetRandomScreeningConfigurationValuesFail({ error })))
        )
      )
    );
  });

  confirmUpdateRandomScreeningConfigurationValue = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ConfirmUpdateRandomScreeningConfigurationValue),
      switchMap(() =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to update this configuration value?`,
              dismissText: `Dismiss`,
              isDestructive: false
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return of(DriverActions.UpdateRandomScreeningConfigurationValue());
              } else {
                return of(DriverActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  updateRandomScreeningConfigurationValue = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.UpdateRandomScreeningConfigurationValue),
      withLatestFrom(this.store$.select(driverState)),
      switchMap(([_, state]) =>
        this.randomScreeningDriverSelectionService.updateRuleValue(state.currentlyEditedRandomScreeningConfigurationValue).pipe(
          map(ruleValue => {
            return DriverActions.UpdateRandomScreeningConfigurationValueSuccess({ ruleValue });
          }),
          catchError(error => of(DriverActions.UpdateRandomScreeningConfigurationValueFail({ error })))
        )
      )
    );
  });

  getDriverTaxDocuments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetDriverTaxDocuments),
      withLatestFrom(this.store$.select(driverState)),
      filter(([_, state]) => this.authDataService.hasPermission({ any: [PermissionsConstants.DRIVER_TAX_INFORMATION_READ], all: [] })),
      switchMap(([action, state]) =>
        this.dotsDriverService.listTaxDocumentByDriverIdAndTaxYear(state.driver.dotsDriverIdentifier, action.taxYear).pipe(
          map(taxDocuments => DriverActions.GetDriverTaxDocumentsSuccess({ taxDocuments })),
          catchError(error => of(DriverActions.GetDriverTaxDocumentsFail({ error })))
        )
      )
    );
  });

  getRefusalReasons$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.GetRefusalReasons),
      switchMap(() =>
        this.refusalReasonService.listRefusalReasons().pipe(
          map(refusalReasons => {
            return DriverActions.GetRefusalReasonsSuccess({ refusalReasons });
          }),
          catchError(error => of(DriverActions.GetRefusalReasonsFail({ error })))
        )
      )
    );
  });

  confirmCreateNewRandomScreening = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.ConfirmCreateNewRandomScreening),
      switchMap(action =>
        this.dialog
          .open(CreateRandomSubstanceScreeningDialogComponent)
          .afterClosed()
          .pipe(
            switchMap(result => {
              if (result) {
                return of(
                  DriverActions.CreateRandomSubstanceScreening({
                    dotsDriverId: action.dotsDriverId,
                    screeningPackageCode: result.testType
                  })
                );
              } else {
                return of(DriverActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  goToDriver$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DriverActions.GoToDriver),
        map(action => this.router.navigate([`/driver-management/driver-detail/${action.dotsDriver.id}`]))
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store$: Store<State<DriverState>>,
    private driverManagementService: DriverManagementService,
    private dotsDriverService: DotsDriverService,
    private safeUrlPipe: SafeUrlPipe,
    private driverBankService: DriverBankService,
    private customerServiceNoteService: CustomerServiceNoteService,
    private dialog: MatDialog,
    private currencyPipe: CurrencyPipe,
    private driverService: DriverService,
    private driverSuspensionService: DriverSuspensionService,
    private randomScreeningDriverSelectionService: RandomScreeningDriverSelectionService,
    private refusalReasonService: RefusalReasonService,
    private userService: UserService,
    private personService: PersonService,
    private authDataService: AuthDataService,
    private router: Router
  ) {}
}
